/* 
🎯 Shell UI Design System v3.0 - Minimal Background System
Purpose: Replace complex 11-layer atmospheric system with minimal brutalist approach
Philosophy: Function-first, minimal decoration, essential elements only

DOCUMENTATION:
This component provides a minimal background system aligned with the new brutalist
design philosophy. It replaces the previous 11-layer particle/wave/glow system
with a clean, functional approach focused on content over decoration.

MINIMAL LAYERS:
1. Dot pattern overlay (handled by body::before in globals.css)
2. Optional subtle grid overlay
3. Glassmorphic section backgrounds (provided as utility)

USAGE:
Unlike the previous FixedBackgroundSystem, this component is optional and minimal.
Most shells will rely on the dot pattern from globals.css and glassmorphic containers.

PHILOSOPHY ALIGNMENT:
- Function-first: Background serves content, doesn't compete
- Minimal decoration: Only essential visual elements
- Brutalist approach: Honest, undecorated surfaces
*/

import React from 'react';

export const MinimalBackgroundSystem = ({
  // Minimal control options
  showGrid = false,           // Optional subtle grid overlay
  gridSize = '48px',          // Grid size for optional overlay
  gridOpacity = 0.06,         // Grid opacity (subtle but visible)
  className = '',
  children,
  ...props
}) => {
  return (
    <div
      className={`minimal-background-system ${className}`}
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--background, #121212)',
        color: 'var(--text-primary, rgba(250, 249, 246, 0.9))',
        position: 'relative',
        ...props.style
      }}
      {...props}
    >
      {/* Optional Grid Overlay - Uses white lines for dark mode */}
      {showGrid && (
        <div
          className="grid-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `
              linear-gradient(rgba(250, 249, 246, ${gridOpacity}) 1px, transparent 1px),
              linear-gradient(90deg, rgba(250, 249, 246, ${gridOpacity}) 1px, transparent 1px)
            `,
            backgroundSize: `${gridSize} ${gridSize}`,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
      )}

      {/* Content area - above grid */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

export const GlasmorphicSection = ({ 
  children, 
  variant = 'default',
  className = '',
  ...props 
}) => {
  const baseClass = 'glassmorphic-section';
  const variantClass = variant !== 'default' ? `${baseClass}--${variant}` : '';
  
  const combinedClassName = [baseClass, variantClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={combinedClassName} {...props}>
      {children}
    </section>
  );
};

export const CenteredContent = ({ 
  children, 
  maxWidth = '1200px',
  padding = 'lg',
  className = '',
  ...props 
}) => {
  const baseClass = 'centered-content';
  const paddingClass = `${baseClass}--padding-${padding}`;
  
  const combinedClassName = [baseClass, paddingClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div 
      className={combinedClassName} 
      style={{ maxWidth, margin: '0 auto', ...props.style }}
      {...props}
    >
      {children}
    </div>
  );
};

export default MinimalBackgroundSystem;

/* 
UPDATE HISTORY:
- v3.0.0: Complete replacement of 11-layer atmospheric system
- v3.0.0: Minimal brutalist approach aligned with new brand philosophy
- v3.0.0: Optional grid overlay for layout assistance
- v3.0.0: Glassmorphic section utility for content areas

MIGRATION NOTES:
This component replaces FixedBackgroundSystem. The new approach is:
1. Much lighter and performance-focused
2. Aligned with brutalist/minimal philosophy  
3. Allows content to be the hero, not decoration
4. Maintains responsive and accessibility features

PHILOSOPHY:
"Function-first philosophy - every element serves a purpose. Decoration only 
exists if it clarifies function. Strip to essentials, then amplify with 
scale and contrast."
*/